import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import LoginPage from '@/pages/Login';
import Register from '@/pages/Register';
import AcceptInvite from '@/pages/AcceptInvite';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import ApplyKyc from '@/pages/ApplyKyc';
import ApproveDevice from '@/pages/ApproveDevice';
import LandlordReview from '@/pages/LandlordReview';
import { ProtectedRoute, RootRedirect, DashboardGuard, RouteErrorBoundary } from '@/components/common';
import NotFound from '@/pages/NotFound';

const PublicPortal = lazy(() => import('@/pages/PublicPortal'));

const MarketingHome = lazy(() => import('@/pages/marketing/MarketingHome'));
const MarketingPricing = lazy(() => import('@/pages/marketing/MarketingPricing'));
const MarketingAbout = lazy(() => import('@/pages/marketing/MarketingAbout'));
const Terms = lazy(() => import('@/pages/marketing/Terms'));
const Privacy = lazy(() => import('@/pages/marketing/Privacy'));

const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Properties = lazy(() => import('@/pages/Properties'));
const Buildings = lazy(() => import('@/pages/Buildings'));
const BuildingDetail = lazy(() => import('@/pages/BuildingDetail'));
const Users = lazy(() => import('@/pages/Users'));
const Tenants = lazy(() => import('@/pages/Tenants'));
const MoveInOut = lazy(() => import('@/pages/MoveInOut'));
const TenantManagement = lazy(() => import('@/pages/TenantManagement'));
const Analytics = lazy(() => import('@/pages/Analytics'));
const Invoices = lazy(() => import('@/pages/Invoices'));
const Inspections = lazy(() => import('@/pages/Inspections'));
const Maintenance = lazy(() => import('@/pages/Maintenance'));
const Documents = lazy(() => import('@/pages/Documents'));
const Leases = lazy(() => import('@/pages/Leases'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const Audit = lazy(() => import('@/pages/Audit'));
const Reports = lazy(() => import('@/pages/Reports'));
const Settings = lazy(() => import('@/pages/Settings'));
const Import = lazy(() => import('@/pages/Import'));
const CRM = lazy(() => import('@/pages/CRM'));
const OwnerPortal = lazy(() => import('@/pages/OwnerPortal'));
const ClientApplication = lazy(() => import('@/pages/ClientApplication'));
const ServiceCharges = lazy(() => import('@/pages/ServiceCharges'));
const Finance = lazy(() => import('@/pages/Finance'));
const FinanceAccounts = lazy(() => import('@/pages/FinanceAccounts'));
const FinanceExpenses = lazy(() => import('@/pages/FinanceExpenses'));
const FinanceReceipts = lazy(() => import('@/pages/FinanceReceipts'));
const FinanceBulkInvoices = lazy(() => import('@/pages/FinanceBulkInvoices'));
const FinanceAging = lazy(() => import('@/pages/FinanceAging'));
const FinanceReports = lazy(() => import('@/pages/FinanceReports'));
const FinanceReminders = lazy(() => import('@/pages/FinanceReminders'));
const Organisations = lazy(() => import('@/pages/Organisations'));
const ComponentDemo = lazy(() => import('@/pages/ComponentDemo'));
const Help = lazy(() => import('@/pages/Help'));
const SupportTicketsAdmin = lazy(() => import('@/pages/SupportTicketsAdmin'));
const PricingCampaignsAdmin = lazy(() => import('@/pages/PricingCampaignsAdmin'));
const Referrals = lazy(() => import('@/pages/Referrals'));
const AmbassadorDashboard = lazy(() => import('@/pages/AmbassadorDashboard'));
const AmbassadorKyc = lazy(() => import('@/pages/AmbassadorKyc'));
const AmbassadorsAdmin = lazy(() => import('@/pages/AmbassadorsAdmin'));

export const router = createBrowserRouter([
  {
    // Pathless wrapper purely so every route below shares one errorElement,
    // without it, an unhandled loader/render error anywhere falls through to
    // React Router's own unstyled default error screen instead of this one.
    // Doesn't change path matching: absolute child paths resolve exactly as
    // before, same as how ProtectedRoute's own pathless layout already works.
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/',
        element: <RootRedirect />,
      },
      {
        // Pathless layout route, children below carry their own absolute paths.
        element: <ProtectedRoute />,
        children: [
          {
            element: <DashboardGuard />,
            children: [
              {
                path: '/dashboard',
                element: <Dashboard />,
              },
            ],
          },
          {
            path: '/properties',
            element: <Properties />,
          },
          {
            path: '/buildings',
            element: <Buildings />,
          },
          {
            path: '/buildings/:id',
            element: <BuildingDetail />,
          },
          {
            path: '/import',
            element: <Import />,
          },
          {
            path: '/users',
            element: <Users />,
          },
          {
            path: '/tenants',
            element: <Tenants />,
          },
          {
            path: '/tenants/portal',
            element: <TenantManagement />,
          },
          {
            path: '/tenants/application',
            element: <ClientApplication />,
          },
          {
            path: '/tenants/move-in-out',
            element: <MoveInOut />,
          },
          {
            path: '/leases',
            element: <Leases />,
          },
          {
            path: '/invoices',
            element: <Invoices />,
          },
          {
            path: '/inspections',
            element: <Inspections />,
          },
          {
            path: '/maintenance',
            element: <Maintenance />,
          },
          { path: '/documents', element: <Documents /> },
          { path: '/documents/generator', element: <Documents /> },
          { path: '/documents/templates', element: <Documents /> },
          { path: '/documents/correspondence', element: <Documents /> },
          { path: '/documents/email-wording', element: <Documents /> },
          {
            path: '/analytics',
            element: <Analytics />,
          },
          { path: '/notifications', element: <Notifications /> },
          { path: '/notifications/preferences', element: <Notifications /> },
          { path: '/notifications/broadcast', element: <Notifications /> },
          { path: '/notifications/delivery-audit', element: <Notifications /> },
          {
            path: '/audit',
            element: <Audit />,
          },
          {
            path: '/reports',
            element: <Reports />,
          },
          { path: '/settings', element: <Navigate to="/settings/security" replace /> },
          { path: '/settings/assets', element: <Settings /> },
          { path: '/settings/public-api', element: <Settings /> },
          { path: '/settings/whatsapp', element: <Settings /> },
          { path: '/settings/assignments', element: <Settings /> },
          { path: '/settings/plan', element: <Settings /> },
          { path: '/settings/security', element: <Settings /> },
          { path: '/settings/faqs', element: <Settings /> },
          { path: '/settings/service-charges', element: <Settings /> },
          { path: '/settings/rent-disbursement', element: <Settings /> },
          { path: '/settings/data-export', element: <Settings /> },
          {
            path: '/crm',
            element: <CRM />,
          },
          {
            path: '/owner-portal',
            element: <OwnerPortal />,
          },
          {
            path: '/service-charges',
            element: <ServiceCharges />,
          },
          { path: '/finance', element: <Finance /> },
          { path: '/finance/accounts', element: <FinanceAccounts /> },
          { path: '/finance/expenses', element: <FinanceExpenses /> },
          { path: '/finance/receipts', element: <FinanceReceipts /> },
          { path: '/finance/bulk-invoices', element: <FinanceBulkInvoices /> },
          { path: '/finance/aging', element: <FinanceAging /> },
          { path: '/finance/reports', element: <FinanceReports /> },
          { path: '/finance/reminders', element: <FinanceReminders /> },
          {
            path: '/platform/organisations',
            element: <Organisations />,
          },
          {
            path: '/platform/overview',
            element: <Dashboard />,
          },
          {
            path: '/platform/analytics',
            element: <Analytics />,
          },
          {
            path: '/platform/support-tickets',
            element: <SupportTicketsAdmin />,
          },
          {
            path: '/platform/pricing-campaigns',
            element: <PricingCampaignsAdmin />,
          },
          {
            path: '/referrals',
            element: <Referrals />,
          },
          {
            path: '/platform/ambassadors',
            element: <AmbassadorsAdmin />,
          },
          {
            path: '/ambassador',
            element: <AmbassadorDashboard />,
          },
          {
            path: '/ambassador/apply',
            element: <AmbassadorKyc mode="embedded" />,
          },
          {
            path: '/help',
            element: <Help />,
          },
          { path: '/help/:topicId', element: <Help /> },
          { path: '/help/:topicId/:articleId', element: <Help /> },
          {
            path: '/demo',
            element: <ComponentDemo />,
          },
        ],
      },
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/register',
        element: <Register />,
      },
      {
        path: '/become-an-ambassador',
        element: <AmbassadorKyc mode="standalone" />,
      },
      {
        path: '/accept-invite',
        element: <AcceptInvite />,
      },
      {
        path: '/forgot-password',
        element: <ForgotPassword />,
      },
      {
        path: '/reset-password',
        element: <ResetPassword />,
      },
      {
        path: '/apply/:token',
        element: <ApplyKyc />,
      },
      {
        path: '/approve-device/:token',
        element: <ApproveDevice />,
      },
      {
        path: '/landlord-review/:token',
        element: <LandlordReview />,
      },
      {
        path: '/public/:slug',
        element: <PublicPortal />,
      },
      {
        path: '/home',
        element: <MarketingHome />,
      },
      {
        path: '/hm/pricing',
        element: <MarketingPricing />,
      },
      {
        path: '/hm/about',
        element: <MarketingAbout />,
      },
      {
        path: '/terms',
        element: <Terms />,
      },
      {
        path: '/privacy',
        element: <Privacy />,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
]);
